/**
 * CohortAnalysisTable
 *
 * Signup to Paying Customer Conversion analysis table.
 * Shows monthly cohort data with conversion rates.
 */
import React from 'react';
import { Info } from 'lucide-react';
import AnalyticsTooltip from './AnalyticsTooltip';

const CohortAnalysisTable = ({ analyticsData, formatPercentage, formatCurrency }) => {
  if (!analyticsData.cohortAnalysis || analyticsData.cohortAnalysis.length === 0) {
    return null;
  }

  return (
    <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Signup → Paying Customer Conversion
        </h3>
        <AnalyticsTooltip content="Shows how many users who signed up each month eventually became paying customers. Higher conversion rates indicate better onboarding and product-market fit." id="cohort-tooltip">
          <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
        </AnalyticsTooltip>
      </div>
      
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        Track your conversion funnel by month: <strong>Total Signups</strong> → <strong>Paying Customers</strong> = <strong>Conversion Rate</strong>
      </p>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800/50 backdrop-blur-md">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Month
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Total Signups
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Became Paying
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Conversion Rate
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Revenue Per Convert
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-transparent backdrop-blur-md divide-y divide-gray-200 dark:divide-gray-700">
            {analyticsData.cohortAnalysis.slice(-6).map((cohort) => (
              <tr key={cohort.month} className="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  <div className="flex flex-col">
                    <span className="font-semibold">{new Date(cohort.month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{cohort.month}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-900 dark:text-white">{cohort.signups.toLocaleString()}</span>
                    <span className="text-xs">users signed up</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex flex-col">
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400">{cohort.conversions.toLocaleString()}</span>
                    <span className="text-xs">now paying</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                      cohort.conversionRate > 20 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : cohort.conversionRate > 10
                        ? 'bg-yellow-100/70 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                      {formatPercentage(cohort.conversionRate)}
                    </span>
                    {cohort.conversionRate > 20 && (
                      <span className="text-green-600 dark:text-green-400 text-xs">🎉 Great!</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-900 dark:text-white">{formatCurrency(cohort.averageRevenue)}</span>
                    <span className="text-xs">per customer</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <div className="mt-4 p-4 bg-blue-50 dark:bg-transparent backdrop-blur-md rounded-lg">
        <p className="text-sm text-blue-700 dark:text-blue-300">
          <strong>What this shows:</strong> For each month, how many people signed up vs. how many eventually became paying customers. 
          Higher conversion rates (green) indicate better onboarding, pricing, or product-market fit for that time period.
        </p>
      </div>
    </div>
  );
};

export default CohortAnalysisTable;


